import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const MenuCtx = createContext<{ close: () => void }>({ close: () => {} });

interface MenuProps {
  /** Content of the trigger button. */
  trigger: React.ReactNode;
  /** Accessible name for the trigger. */
  label: string;
  align?: 'left' | 'right';
  triggerClassName?: string;
  children: React.ReactNode;
}

export function Menu({ trigger, label, align = 'right', triggerClassName = '', children }: MenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={triggerClassName || 'btn btn-ghost btn-icon'}
      >
        {trigger}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute z-50 mt-1 min-w-[13rem] overflow-hidden rounded-[var(--radius)] border border-[var(--card-border)] bg-[var(--bg-secondary)] py-1 shadow-[var(--shadow-lg)] ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <MenuCtx.Provider value={{ close: () => setOpen(false) }}>{children}</MenuCtx.Provider>
        </div>
      )}
    </div>
  );
}

interface MenuItemProps {
  onClick?: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
  children: React.ReactNode;
}

export function MenuItem({ onClick, icon, danger, children }: MenuItemProps) {
  const { close } = useContext(MenuCtx);
  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        onClick?.();
        close();
      }}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[0.8125rem] font-medium transition-colors hover:bg-[var(--card-bg-subtle)] ${
        danger ? 'text-[var(--status-ng-text)]' : 'text-[var(--text-main)]'
      }`}
    >
      {icon && <span className="flex-shrink-0 text-[var(--text-dim)]">{icon}</span>}
      <span className="truncate">{children}</span>
    </button>
  );
}

export function MenuSeparator() {
  return <div className="my-1 border-t border-[var(--border-color)]" />;
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 py-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-[var(--text-dim)]">
      {children}
    </div>
  );
}
